import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const ProjectDetails = () => {
  const { id } = useParams(); // This is the slug or ID from URL
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiDebug, setApiDebug] = useState(null);

  useEffect(() => {
    if (id) {
      fetchProjectDetails();
    }
  }, [id]);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      setApiDebug(null);
      
      // Use your configured API endpoint
      const apiUrl = `${API_ENDPOINTS.PROJECTS}?id=${encodeURIComponent(id)}`;
      console.log('Fetching from:', apiUrl);
      
      const response = await axios.get(apiUrl);
      console.log('Full API Response:', response);
      
      // Store debug info
      setApiDebug({
        url: apiUrl,
        status: response.status,
        data: response.data
      });
      
      if (response.data.success && response.data.data) {
        setProject(response.data.data);
      } else {
        setError(response.data.message || 'Project not found in database');
      }
    } catch (error) {
      console.error('API Error details:', error);
      
      // Store debug info for error
      setApiDebug({
        url: error.config?.url,
        status: error.response?.status,
        error: error.message,
        response: error.response?.data
      });
      
      // Handle specific errors
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 404) {
          setError('Project not found. Please check the project URL.');
        } else if (error.response.status === 500) {
          setError('Server error. The database query failed.');
        } else {
          setError(`Server error (${error.response.status}): ${error.response.data?.message || 'Unknown error'}`);
        }
      } else if (error.request) {
        // No response received
        setError('Cannot connect to the server. Please check if the backend is running.');
      } else {
        // Request setup error
        setError('Request error: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === 'null') return 'Ongoing';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Ongoing';
    }
  };

  const getStatusBadge = () => {
    if (!project) return null;
    const isOngoing = project.category === 'ongoing';
    return (
      <span className={`badge ${isOngoing ? 'bg-info' : 'bg-success'} me-2`}>
        {isOngoing ? 'Ongoing' : 'Completed'}
      </span>
    );
  };

  const getFeaturedBadge = () => {
    if (!project || !project.is_featured) return null;
    return (
      <span className="badge bg-warning text-dark me-2">
        <i className="bi bi-star-fill me-1"></i> Featured
      </span>
    );
  };

  const retryFetch = () => {
    fetchProjectDetails();
  };

  const testApiDirectly = () => {
    if (!id) return;
    const testUrl = `${API_ENDPOINTS.PROJECTS}?id=${id}`;
    window.open(testUrl, '_blank');
    console.log('Test URL:', testUrl);
  };

  // Debug view for API issues
  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Failed to Load Project</h4>
          <p className="mb-2">{error}</p>
          <p className="mb-0">Trying to load: <code>{id}</code></p>
        </div>
        
        <div className="d-flex gap-2 mb-4">
          <button className="btn btn-primary" onClick={retryFetch}>
            <i className="bi bi-arrow-clockwise me-2"></i>Retry
          </button>
          <button className="btn btn-outline-secondary" onClick={testApiDirectly}>
            <i className="bi bi-bug me-2"></i>Test API
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/projects')}>
            Back to Projects
          </button>
        </div>
        
        {/* Debug Information */}
        {apiDebug && (
          <div className="card border-danger">
            <div className="card-header bg-danger text-white">
              <h5 className="mb-0">Debug Information</h5>
            </div>
            <div className="card-body">
              <h6>API Request:</h6>
              <pre className="bg-light p-3 rounded mb-3">
                {apiDebug.url || 'No URL'}
              </pre>
              
              <h6>Response Status:</h6>
              <p className="mb-3">
                {apiDebug.status ? `HTTP ${apiDebug.status}` : 'No response'}
              </p>
              
              {apiDebug.response && (
                <>
                  <h6>Server Response:</h6>
                  <pre className="bg-light p-3 rounded">
                    {JSON.stringify(apiDebug.response, null, 2)}
                  </pre>
                </>
              )}
              
              {apiDebug.error && (
                <>
                  <h6>Error Details:</h6>
                  <pre className="bg-light p-3 rounded">
                    {apiDebug.error}
                  </pre>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">Project Not Found</h4>
          <p>No project found with identifier: <code>{id}</code></p>
          <p className="mb-0">This project might not exist or is not active.</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/projects')}>
          View All Projects
        </button>
      </div>
    );
  }

  return (
    <div className="project-details-page">
      {/* Hero Section */}
      <section className="hero-section" style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
        color: 'white',
        padding: '5rem 0'
      }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <button 
                className="btn btn-outline-light mb-4"
                onClick={() => navigate('/projects')}
              >
                <i className="bi bi-arrow-left me-2"></i> Back to Projects
              </button>
              
              <div className="d-flex align-items-center flex-wrap mb-3 gap-2">
                {getFeaturedBadge()}
                {getStatusBadge()}
                {project.thematic_area_name && (
                  <span className="badge bg-light text-dark">
                    {project.thematic_area_name}
                  </span>
                )}
              </div>
              
              <h1 className="display-4 fw-bold mb-4">{project.title}</h1>
              <p className="lead mb-4 opacity-90">{project.description}</p>
              
              <div className="d-flex flex-wrap gap-4">
                {project.location && (
                  <div className="d-flex align-items-center">
                    <i className="bi bi-geo-alt me-2 fs-5"></i>
                    <span className="fs-5">{project.location}</span>
                  </div>
                )}
                
                {project.beneficiaries && (
                  <div className="d-flex align-items-center">
                    <i className="bi bi-people me-2 fs-5"></i>
                    <span className="fs-5">{project.beneficiaries} Beneficiaries</span>
                  </div>
                )}
                
                <div className="d-flex align-items-center">
                  <i className="bi bi-calendar me-2 fs-5"></i>
                  <span className="fs-5">
                    {formatDate(project.start_date)} - {formatDate(project.end_date)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            {/* Project Content */}
            <div className="col-lg-8">
              <div className="project-content mb-5">
                <h2 className="mb-4">Project Details</h2>
                {project.full_content ? (
                  <div 
                    className="content-html"
                    dangerouslySetInnerHTML={{ __html: project.full_content }}
                  />
                ) : (
                  <>
                    <p className="fs-5">{project.description}</p>
                    <div className="alert alert-info">
                      <i className="bi bi-info-circle me-2"></i>
                      Detailed project documentation is being prepared.
                    </div>
                  </>
                )}
              </div>
              
              {/* Project Image */}
              {project.image_path && (
                <div className="mb-5">
                  <div className="ratio ratio-16x9">
                    <img 
                      src={project.image_path.startsWith('http') 
                        ? project.image_path 
                        : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost/sikom-ngo-website/backend'}/${project.image_path.replace(/^\.\.\//, '')}`
                      }
                      alt={project.title}
                      className="img-fluid rounded shadow"
                      style={{ objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.src = 'https://placehold.co/800x450/1e40af/ffffff?text=Project+Image';
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Sidebar */}
            <div className="col-lg-4">
              {/* Project Info Card */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                  <h5 className="card-title border-bottom pb-3 mb-4">
                    <i className="bi bi-info-circle me-2"></i>
                    Project Information
                  </h5>
                  
                  <div className="mb-4">
                    <h6 className="text-muted mb-2">Status</h6>
                    <div>{getStatusBadge()}</div>
                  </div>
                  
                  {project.location && (
                    <div className="mb-4">
                      <h6 className="text-muted mb-2">
                        <i className="bi bi-geo-alt me-1"></i> Location
                      </h6>
                      <p className="mb-0">{project.location}</p>
                    </div>
                  )}
                  
                  {project.beneficiaries && (
                    <div className="mb-4">
                      <h6 className="text-muted mb-2">
                        <i className="bi bi-people me-1"></i> Beneficiaries
                      </h6>
                      <p className="mb-0">{project.beneficiaries}</p>
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <h6 className="text-muted mb-2">
                      <i className="bi bi-calendar me-1"></i> Duration
                    </h6>
                    <p className="mb-0">
                      {formatDate(project.start_date)} to {formatDate(project.end_date)}
                    </p>
                  </div>
                  
                  {project.funding_partner && (
                    <div className="mb-4">
                      <h6 className="text-muted mb-2">
                        <i className="bi bi-building me-1"></i> Funding Partner
                      </h6>
                      <p className="mb-0">{project.funding_partner}</p>
                    </div>
                  )}
                  
                  {project.thematic_area_name && (
                    <div className="mb-4">
                      <h6 className="text-muted mb-2">
                        <i className="bi bi-tag me-1"></i> Thematic Area
                      </h6>
                      <p className="mb-0">{project.thematic_area_name}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Share Card */}
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h5 className="card-title mb-3">
                    <i className="bi bi-share me-2"></i>
                    Share This Project
                  </h5>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-primary btn-lg flex-fill">
                      <i className="bi bi-facebook"></i>
                    </button>
                    <button className="btn btn-outline-info btn-lg flex-fill">
                      <i className="bi bi-twitter-x"></i>
                    </button>
                    <button className="btn btn-outline-success btn-lg flex-fill">
                      <i className="bi bi-whatsapp"></i>
                    </button>
                    <button className="btn btn-outline-secondary btn-lg flex-fill">
                      <i className="bi bi-link-45deg"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-5">
            <h2 className="mb-0">Other Projects</h2>
            <Link to="/projects" className="btn btn-outline-primary">
              View All Projects
            </Link>
          </div>
          
          <div className="row g-4">
            {/* These would ideally come from an API call for related projects */}
            <div className="col-md-6 col-lg-4">
              <div className="card h-100 border-0 shadow-sm hover-shadow transition">
                <div className="card-body">
                  <span className="badge bg-info mb-2">Ongoing</span>
                  <h5 className="card-title">Youth Empowerment Program</h5>
                  <p className="card-text text-muted">Skills training and entrepreneurship for youth in urban slums.</p>
                  <Link to="/projects/youth-empowerment-program" className="btn btn-sm btn-outline-primary">
                    View Details
                  </Link>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-4">
              <div className="card h-100 border-0 shadow-sm hover-shadow transition">
                <div className="card-body">
                  <span className="badge bg-success mb-2">Completed</span>
                  <h5 className="card-title">Women's Economic Empowerment</h5>
                  <p className="card-text text-muted">Supporting women-led businesses and micro-enterprises.</p>
                  <Link to="/projects/womens-economic-empowerment" className="btn btn-sm btn-outline-primary">
                    View Details
                  </Link>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-4">
              <div className="card h-100 border-0 shadow-sm hover-shadow transition">
                <div className="card-body">
                  <span className="badge bg-info mb-2">Ongoing</span>
                  <h5 className="card-title">Environmental Conservation</h5>
                  <p className="card-text text-muted">Tree planting and environmental awareness campaigns.</p>
                  <Link to="/projects/environmental-conservation" className="btn btn-sm btn-outline-primary">
                    View Details
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetails;